import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import {
  Stethoscope,
  MapPin,
  Shield,
  Building2,
  Activity,
  Sparkles,
  ArrowRight,
  Search,
} from "lucide-react";
import {
  getLandingDirectory,
  getDirectoryTotal,
  type DirectoryCategory,
} from "@/lib/landing-directory";

const SITE = "https://heallexa.com";

const ICONS = {
  Stethoscope,
  MapPin,
  Shield,
  Building2,
  Activity,
  Sparkles,
} as const;

export const Route = createFileRoute("/landing-pages/directory/")({
  head: () => {
    const title = "Landing Pages Directory | Heallexa";
    const description =
      "Browse every Heallexa landing page: specialty + city, specialty + state, insurance + city, conditions, services and more.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: `${SITE}/landing-pages/directory` },
      ],
      links: [{ rel: "canonical", href: `${SITE}/landing-pages/directory` }],
    };
  },
  component: LandingDirectoryIndex,
});

function LandingDirectoryIndex() {
  const categories = useMemo(() => getLandingDirectory(), []);
  const total = useMemo(() => getDirectoryTotal(categories), [categories]);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return categories;
    return categories
      .map((c) => ({
        ...c,
        links: c.links.filter((l) => l.label.toLowerCase().includes(q)),
      }))
      .filter((c) => c.links.length > 0);
  }, [categories, q]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-sky-100/40 to-background dark:from-primary/20 dark:via-primary/5 dark:to-background" />
        <div className="absolute -top-32 -right-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" aria-hidden />
        <div className="absolute -bottom-32 -left-24 h-80 w-80 rounded-full bg-sky-300/30 blur-3xl" aria-hidden />

        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-20">
          <nav className="text-sm text-muted-foreground mb-6">
            <Link to="/sitemap" className="hover:text-primary">Sitemap</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">Landing Pages</span>
          </nav>

          <p className="text-sm font-medium text-primary mb-2">Heallexa Directory</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground">
            The full Heallexa
            <br />
            <span className="bg-gradient-to-r from-primary to-sky-500 bg-clip-text text-transparent">
              landing page index
            </span>
          </h1>
          <p className="mt-5 text-muted-foreground max-w-2xl text-lg">
            {total.toLocaleString()} programmatic pages across specialties, cities,
            states, insurance plans, conditions and services — all browseable below.
          </p>

          {/* Search */}
          <div className="mt-8 relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter pages — e.g. 'cardiologist Dallas' or 'Aetna'"
              className="w-full rounded-2xl border border-border bg-card pl-11 pr-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {/* TOC chips */}
          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                className="px-3 py-1.5 rounded-full border border-border bg-card/70 backdrop-blur text-xs font-medium text-foreground hover:bg-primary/10 hover:border-primary/30 transition"
              >
                {c.title}{" "}
                <span className="text-muted-foreground">
                  {c.links.length.toLocaleString()}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Overview cards */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c) => (
            <CategoryCard key={c.id} category={c} />
          ))}
        </div>
      </section>

      {/* Full link sections */}
      <section className="mx-auto max-w-6xl px-6 pb-20 space-y-12">
        {filtered.length === 0 && (
          <div className="rounded-2xl border border-border bg-card p-10 text-center">
            <p className="text-muted-foreground">
              No pages matched <span className="font-medium text-foreground">"{query}"</span>.
            </p>
          </div>
        )}

        {filtered.map((c) => (
          <CategorySection key={c.id} category={c} maxVisible={q ? 9999 : 60} />
        ))}
      </section>

      <FooterSection />
    </div>
  );
}

function CategoryCard({ category }: { category: DirectoryCategory }) {
  const Icon = ICONS[category.iconName];
  const sample = category.links.slice(0, 5);
  return (
    <a
      href={`#${category.id}`}
      className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-md transition"
    >
      <div
        className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${category.accent}`}
        aria-hidden
      />
      <div className="flex items-center gap-3 mb-3">
        <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${category.accent} flex items-center justify-center`}>
          <Icon className="h-5 w-5 text-foreground" />
        </div>
        <div>
          <h2 className="font-semibold text-foreground leading-tight">{category.title}</h2>
          <p className="text-xs text-muted-foreground">
            {category.links.length.toLocaleString()} pages
          </p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">{category.blurb}</p>
      <ul className="space-y-1">
        {sample.map((l) => (
          <li key={l.href} className="text-xs text-muted-foreground truncate">
            · {l.label}
          </li>
        ))}
      </ul>
      <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-primary group-hover:gap-2 transition-all">
        Browse all <ArrowRight className="h-3 w-3" />
      </div>
    </a>
  );
}

function CategorySection({
  category,
  maxVisible,
}: {
  category: DirectoryCategory;
  maxVisible: number;
}) {
  const Icon = ICONS[category.iconName];
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? category.links : category.links.slice(0, maxVisible);
  const canExpand = category.links.length > maxVisible;

  return (
    <section id={category.id} className="scroll-mt-24">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${category.accent} flex items-center justify-center`}>
            <Icon className="h-5 w-5 text-foreground" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">
              {category.title}
            </h2>
            <p className="text-sm text-muted-foreground">{category.blurb}</p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {category.links.length.toLocaleString()} total
        </span>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
          {shown.map((l) => (
            <li key={l.href} className="text-sm">
              <a
                href={l.href}
                className="block py-1 text-foreground hover:text-primary hover:underline truncate"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        {canExpand && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:gap-2 transition-all"
          >
            {expanded
              ? "Show fewer"
              : `Show all ${category.links.length.toLocaleString()}`}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </section>
  );
}
